import { useEffect, useMemo, useRef, useState } from 'react';
import { useSave } from '@/ui/services/ServicesContext';

/** The player's "Giảm chuyển động" setting, falling back to the system preference. */
export function useReducedMotion(): boolean {
  const pref = useSave((s) => s.settings.reducedMotion);
  const system = useMemo(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches, []);
  return pref === 'on' || (pref === 'system' && system);
}

/**
 * A number that counts up (or down) to `value` over `ms` (ux-screens §3.1: HUD coins/stars count in 400 ms).
 * With `animate` off it is simply `value`, so reduced motion jumps straight to the new number.
 */
export function useCountUp(value: number, animate: boolean, ms = 400): number {
  const [shown, setShown] = useState(value);
  const current = useRef(value);
  useEffect(() => {
    const from = current.current;
    if (!animate || from === value) {
      current.current = value;
      return;
    }
    const start = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const k = Math.min(1, (now - start) / ms);
      const v = Math.round(from + (value - from) * (1 - (1 - k) ** 3));
      current.current = v;
      setShown(v);
      if (k < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(frame);
    };
  }, [value, animate, ms]);
  return animate ? shown : value;
}
